import { CreateInterventionReqData } from '@/shared/types/interventions'
import connector from './connector'
import { objectToFormData } from '@/shared/utils/file-upload-utils'
import {
  IndividualIntervention,
  InterventionDocument,
  InterventionFilterCounts,
  InterventionFullResponse,
  ListInterventionsParams,
  ListInterventionsResponse,
  EntityType,
  DisputeCategory,
  InterventionStatus,
} from '@depanneo/types'
import { get, del, patch } from '@/shared/utils/api-utils'
import { getDocumentsCount } from './documents-api'
import { ReportDisputeData } from '@/hooks/interventions/useReportDisputeMutation'
import {
  JobReportResponse,
  UpdateJobReportData,
} from '@/shared/types/job-reports'

// POST /interventions returns the raw inserted row, NOT a hydrated
// IndividualIntervention (no site/equipment/assignedUsers/creator). Type it as
// the narrow contract callers actually consume — the new intervention's id
// (used to redirect to its detail page, #1193) — so nobody reads detail-only
// fields off the create result at runtime.
const createIntervention = async (
  data: CreateInterventionReqData,
  files?: File[]
): Promise<Pick<IndividualIntervention, 'id'>> => {
  const formData = objectToFormData(
    { ...data, documents: files || [] },
    {
      filesField: 'documents',
      jsonFields: ['temporarySiteData', 'siteContact', 'billingContact'],
    }
  )

  const response = await connector.post('/interventions', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return response.data
}

const updateIntervention = async (
  id: string,
  data: Partial<CreateInterventionReqData>
) => {
  return await patch<
    IndividualIntervention,
    Partial<CreateInterventionReqData>
  >(`/interventions/${id}`, data)
}

const updateInterventionScheduledDate = async (
  id: string,
  data: { scheduledDate: string }
) => {
  return await patch<IndividualIntervention, { scheduledDate: string }>(
    `/interventions/${id}/scheduled-date`,
    data
  )
}

/** #3062: the arrival a contract price uses; SUPER_ADMIN only, null clears it. */
const updateInterventionBilledArrival = async (
  id: string,
  data: { billedArrivalAt: string | null }
) => {
  return await patch<
    IndividualIntervention,
    { billedArrivalAt: string | null }
  >(`/interventions/${id}/billed-arrival`, data)
}

const getInterventionById = async (id: string) => {
  return await get<IndividualIntervention>(`/interventions/${id}`)
}

const getInterventionFull = async (id: string) => {
  return await get<InterventionFullResponse>(`/interventions/${id}/full`)
}

/** Per-file result of a bulk document upload (mirrors the API `UploadResult`). */
export interface UploadInterventionDocumentResult {
  documentId: string
  url: string
  thumbnailUrl: string | null
  mimeType: string
  /** True when the file already existed on the intervention (content dedup). */
  deduplicated: boolean
}

const uploadInterventionDocuments = async (
  interventionId: string,
  files: File[],
  documentTypes: string[]
): Promise<UploadInterventionDocumentResult[]> => {
  // Multipart fields can't carry a real array, so the per-file types travel as
  // a JSON-stringified array under `documentTypes`, index-aligned with the
  // files appended under `documents` (the API re-parses it).
  const formData = objectToFormData(
    { documents: files, documentTypes },
    {
      filesField: 'documents',
      jsonFields: ['documentTypes'],
    }
  )

  const response = await connector.post(
    `/interventions/${interventionId}/documents`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )

  return response.data
}

const getInterventionDocumentsCount = async (interventionId: string) => {
  return await getDocumentsCount(interventionId, EntityType.INTERVENTION)
}

const getInterventionDocuments = async (interventionId: string) => {
  return await get<InterventionDocument[]>(
    `/interventions/${interventionId}/documents`
  )
}

const deleteInterventionDocument = async (
  interventionId: string,
  documentId: string
) => {
  return await del(`/interventions/${interventionId}/documents/${documentId}`)
}

const listInterventions = async (params: ListInterventionsParams = {}) => {
  const queryParams = new URLSearchParams()

  if (params.type?.length) {
    queryParams.append('type', params.type.join(','))
  }
  if (params.priority?.length) {
    queryParams.append('priority', params.priority.join(','))
  }
  if (params.status?.length) {
    queryParams.append('status', params.status.join(','))
  }
  if (params.trade?.length) {
    queryParams.append('trade', params.trade.join(','))
  }
  if (params.dispute?.length) {
    queryParams.append('dispute', params.dispute.join(','))
  }
  if (params.service?.length) {
    queryParams.append('service', params.service.join(','))
  }
  if (params.dispatchFailed) {
    queryParams.append('dispatchFailed', 'true')
  }

  if (params.limit) queryParams.append('limit', params.limit.toString())
  if (params.cursor) queryParams.append('cursor', params.cursor)
  if (params.siteId) queryParams.append('siteId', params.siteId)
  if (params.equipmentId) queryParams.append('equipmentId', params.equipmentId)
  if (params.createdBy) queryParams.append('createdBy', params.createdBy)
  if (params.technicianId)
    queryParams.append('technicianId', params.technicianId)
  if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom)
  if (params.dateTo) queryParams.append('dateTo', params.dateTo)
  if (params.search) queryParams.append('search', params.search)
  if (params.sortBy) queryParams.append('sortBy', params.sortBy)
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)
  if (params.direction) queryParams.append('direction', params.direction)
  if (params.scheduledDateFrom)
    queryParams.append('scheduledDateFrom', params.scheduledDateFrom)
  if (params.scheduledDateTo)
    queryParams.append('scheduledDateTo', params.scheduledDateTo)
  if (params.noPagination) queryParams.append('noPagination', 'true')

  const queryString = queryParams.toString()
  const url = `/interventions${queryString ? `?${queryString}` : ''}`

  return await get<ListInterventionsResponse>(url)
}

const getInterventionFilterCounts = async (params?: {
  technicianId?: string
  crossOrg?: boolean
}) => {
  const queryParams = new URLSearchParams()
  if (params?.technicianId)
    queryParams.append('technicianId', params.technicianId)
  if (params?.crossOrg) queryParams.append('crossOrg', 'true')

  const queryString = queryParams.toString()
  const url = `/interventions/filter-counts${queryString ? `?${queryString}` : ''}`
  return await get<InterventionFilterCounts>(url)
}
export interface DisputeResponse {
  id: string
  hasDispute: boolean
  disputeReportedAt: Date | null
  disputeResolvedAt: Date | null
  disputeCategory: DisputeCategory | null
  disputeReason: string | null
}

async function reportDispute(
  interventionId: string,
  data: ReportDisputeData
): Promise<DisputeResponse> {
  const response = await connector.post<DisputeResponse>(
    `/interventions/${interventionId}/dispute`,
    data
  )
  return response.data
}

// Lifecycle API functions
const putInterventionOnHold = async (
  interventionId: string,
  data: { reason: string; note?: string }
) => {
  const response = await connector.post(
    `/interventions/${interventionId}/hold`,
    data
  )
  return response.data
}

const resumeIntervention = async (interventionId: string) => {
  const response = await connector.post(
    `/interventions/${interventionId}/resume`
  )
  return response.data
}

/**
 * Clears the list's unread marker for the current user (#1552). Fire-and-forget
 * from the detail view: it moves a personal watermark, so a failure is not worth
 * an error toast — the marker just stays until the next open.
 */
const markInterventionRead = async (interventionId: string) => {
  const response = await connector.post<{ lastReadAt: string }>(
    `/interventions/${interventionId}/read`
  )
  return response.data
}

const requestNewQuote = async (interventionId: string) => {
  const response = await connector.post(
    `/interventions/${interventionId}/request-new-quote`
  )
  return response.data
}

const reactivateIntervention = async (interventionId: string) => {
  const response = await connector.post(
    `/interventions/${interventionId}/reactivate`
  )
  return response.data
}
async function resolveDispute(
  interventionId: string
): Promise<DisputeResponse> {
  const response = await connector.post<DisputeResponse>(
    `/interventions/${interventionId}/dispute/resolve`
  )
  return response.data
}

const scheduleNextVisit = async (
  interventionId: string,
  data: { scheduledDate: string }
) => {
  const response = await connector.post(
    `/interventions/${interventionId}/schedule-next-visit`,
    data
  )
  return response.data
}

const assignTechnician = async (
  interventionId: string,
  technicianId: string
) => {
  const response = await connector.post(
    `/interventions/${interventionId}/assign/${technicianId}`
  )
  return response.data
}

const unassignIntervention = async (
  interventionId: string,
  data?: { reason?: string }
) => {
  const response = await connector.delete(
    `/interventions/${interventionId}/assignment`,
    { data }
  )
  return response.data
}

const completeIntervention = async (interventionId: string) => {
  const response = await connector.post(
    `/interventions/${interventionId}/complete`
  )
  return response.data
}

const cancelIntervention = async (
  interventionId: string,
  data?: { reason?: string }
) => {
  const response = await connector.post(
    `/interventions/${interventionId}/cancel`,
    data
  )
  return response.data
}

const closeIntervention = async (interventionId: string) => {
  const response = await connector.post(
    `/interventions/${interventionId}/close`
  )
  return response.data
}

const rejectTechnician = async (
  interventionId: string,
  technicianId: string,
  data?: { reason?: string }
) => {
  const response = await connector.post(
    `/interventions/${interventionId}/reject/${technicianId}`,
    data
  )
  return response.data
}

export interface RejectedTechnicianInfo {
  technicianId: string
  reason: string | null
  rejectedAt: string
}

const getRejectedTechnicians = async (
  interventionId: string
): Promise<RejectedTechnicianInfo[]> => {
  const response = await connector.get<RejectedTechnicianInfo[]>(
    `/interventions/${interventionId}/rejected-technicians`
  )
  return response.data
}

export interface ForceStatusData {
  status: InterventionStatus
  reason: string
}

export interface ForceStatusResponse {
  id: string
  previousStatus: InterventionStatus
  status: InterventionStatus
  reason: string
}

const forceInterventionStatus = async (
  interventionId: string,
  data: ForceStatusData
): Promise<ForceStatusResponse> => {
  return await patch<ForceStatusResponse, ForceStatusData>(
    `/interventions/${interventionId}/force-status`,
    data
  )
}

const getInterventionReports = async (
  interventionId: string
): Promise<JobReportResponse[]> => {
  return await get<JobReportResponse[]>(
    `/interventions/${interventionId}/reports`
  )
}

const updateInterventionReport = async (
  interventionId: string,
  reportId: string,
  data: UpdateJobReportData
): Promise<JobReportResponse> => {
  return await patch<JobReportResponse, UpdateJobReportData>(
    `/interventions/${interventionId}/reports/${reportId}/edit`,
    data
  )
}

const validateInterventionReport = async (
  interventionId: string,
  reportId: string
): Promise<JobReportResponse> => {
  return await patch<JobReportResponse, Record<string, never>>(
    `/interventions/${interventionId}/reports/${reportId}/validate`,
    {}
  )
}

export {
  createIntervention,
  getInterventionById,
  getInterventionFull,
  uploadInterventionDocuments,
  getInterventionDocumentsCount,
  getInterventionDocuments,
  deleteInterventionDocument,
  updateIntervention,
  updateInterventionScheduledDate,
  updateInterventionBilledArrival,
  listInterventions,
  markInterventionRead,
  getInterventionFilterCounts,
  putInterventionOnHold,
  resumeIntervention,
  requestNewQuote,
  reactivateIntervention,
  reportDispute,
  resolveDispute,
  scheduleNextVisit,
  assignTechnician,
  unassignIntervention,
  completeIntervention,
  cancelIntervention,
  closeIntervention,
  rejectTechnician,
  getRejectedTechnicians,
  forceInterventionStatus,
  getInterventionReports,
  updateInterventionReport,
  validateInterventionReport,
}
